import React, { useEffect, useState } from "react";
import { useTranslate } from "./TranslationText";
import NetaqInput from "./NetaqInput";
import ActionButton from "./ActionButton";
import Loader from "./Loader";
import UploadFile from "./UploadFile";
import SimpleUpload from "./SimpleFileUpload";
import { Link } from "react-router-dom";
import { extractChars } from "../helper/extractChart";
import AsyncSelect from "react-select/async";
import axiosInstance from "../axios/axios";
import SectionTitle from "./SectionTitle";
import NewActionButton from "./ActionButtons/NewActionButton";
import EahsForm from "./EahsForm";
import { eformCheckBox, whiteCard } from "../common/cssclasses";

const GrantedPermissionClaimsComponent = ({
  title = "",
  isInOwnerProfile = false,
  permissionsList,
  toast,
  agentPermissionClaimsChanged,
  handleSubmit,
  issubmitting,
}) => {
  const { translate } = useTranslate();

  const [permissionClaims, setPermissionClaims] = useState([
    {
      index: 0,
      selectedPermissions: [],
      isValid: false,
    },
  ]);

  const addNewClaim = () => {
    const newClaim = {
      index: permissionClaims.length,
      selectedPermissions: [],
    };
    setPermissionClaims([...permissionClaims, newClaim]);
  };

  const removeItem = (index) => {
    const leftPart = permissionClaims.slice(0, index);
    const rightPart = permissionClaims.slice(index + 1);
    const newClaims = [...leftPart, ...rightPart];
    setPermissionClaims(newClaims);
  };

  const validateClaim = (claim) => {
    const errorDefinitions = {
      NoPermissionSelected: (claim) =>
        Array.isArray(claim.selectedPermissions) &&
        claim.selectedPermissions.length > 0,
      NoOwnerSelected: (claim) =>
        !!claim.selectedOwner && !!claim.selectedOwner.value,
      NoAuthFilesSelected: (claim) =>
        Array.isArray(claim.agentAuthFiles) && claim.agentAuthFiles.length > 0,
      NoStartAndEndDatesSelected: (claim) => !!claim.fromDate && !!claim.toDate,
    };

    const errors = Object.keys(errorDefinitions).filter((key) => {
      let error = errorDefinitions[key];
      if (!error(claim)) return key;
    });

    return errors;
  };

  const updatePermissionClaim = (index, updatedClaim) => {
    const errors = validateClaim(updatedClaim);
    if (errors.length > 0) {
      updatedClaim.isValid = false;
      updatedClaim.errors = errors;
    } else {
      updatedClaim.isValid = true;
      updatedClaim.errors = [];
    }

    const leftPart = permissionClaims.slice(0, index);
    const rightPart = permissionClaims.slice(index + 1);
    const newClaims = [...leftPart, updatedClaim, ...rightPart];
    setPermissionClaims(newClaims);
  };

  useEffect(() => {
    agentPermissionClaimsChanged(permissionClaims);
  }, [permissionClaims]);

  return (
    <>
      <SectionTitle section_name={title} isContainer={false} noMargin />
      <div className={"bg-white border-15 mb-main"}>
        {permissionClaims.map((claim, index) => {
          return (
            <GrantedPermissionClaimItem
              claim={claim}
              permissionsList={permissionsList}
              canDeleteItem={permissionClaims.length > 1}
              removeItem={removeItem}
              toast={toast}
              index={index}
              updatePermissionClaim={updatePermissionClaim}
              isInOwnerProfile={isInOwnerProfile}
            />
          );
        })}
        <div className="col-lg-12 col-md-12 d-flex col-sm-12 text-right text-left-rtl rotate3D rtl p-main p-3">
          <div className="w-uset ms-auto">
            <NewActionButton
              key={"btn"}
              onClick={addNewClaim}
              text={translate("web.addNewRecord", "Add New Record")}
            />
          </div>
        </div>

        {isInOwnerProfile &&
          (issubmitting ? (
            <Loader />
          ) : (
            <div className="col-lg-12 col-md-12 d-flex col-sm-12 text-right text-left-rtl rotate3D rtl px-3 me-1 pb-3">
              <div className="w-uset ms-auto">
                <NewActionButton
                  type={"submit"}
                  label={"Save"}
                  classNames="px-5 btn-outline-danger"
                  text={translate("web.save", "Save")}
                  onClick={handleSubmit}
                />
              </div>
            </div>
          ))}
      </div>
    </>
  );
};

const GrantedPermissionClaimItem = ({
  claim,
  isInOwnerProfile,
  permissionsList,
  canDeleteItem,
  removeItem,
  toast,
  index,
  updatePermissionClaim,
}) => {
  const [fromDate, setFromDate] = useState();
  const [toDate, setToDate] = useState();
  const [agentAuthFiles, setAgentAuthFiles] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState(claim.selectedOwner);
  const [selectedPermissions, setSelectedPermissions] = useState(
    claim.selectedPermissions
  );
  const { translate } = useTranslate();
  const selectAllOption = {
    value: "all",
    label: translate("web.selectAll", "Select All"),
    text: translate("web.selectAll", "Select All"),
  };

  useEffect(() => {
    setFromDate(claim.fromDate);
    setToDate(claim.toDate);
    setAgentAuthFiles(claim.agentAuthFiles);
    setSelectedPermissions(claim.selectedPermissions);
    setSelectedOwner(claim.selectedOwner);
  }, [claim]);

  const verifyPoAFileSettings = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      let checkFileSize = true;
      const fileListHook = [];

      for (let fileIndex = 0; fileIndex < e.target.files.length; fileIndex++) {
        if (e.target.files[fileIndex].size > 10 * 1024 * 1024) {
          checkFileSize = false;
          toast.error(translate("web.fileSizeLimit", "File size 10MB limit."));
        } else {
          const file = e.target.files[fileIndex];
          const allowedMimeTypes = ["image", "application/pdf"];
          if (!allowedMimeTypes.some((type) => file.type.startsWith(type))) {
            toast.error(
              translate("web.invalidFileType", "Only images and PDFs allowed.")
            );
            return; // Skip to the next file if type is not allowed
          }
          fileListHook.push({
            documentFile: URL.createObjectURL(e.target.files[fileIndex]),
            name: e.target.files[fileIndex].name,
            file: e.target.files[fileIndex],
          });
        }
      }

      setAgentAuthFiles(fileListHook);
      updatePermissionClaim(index, { ...claim, agentAuthFiles: fileListHook });
    }
  };

  // asyunc search
  const [inputValue, setInputValue] = useState("");
  const [timeoutId, setTimeoutId] = useState(0);
  // // Function to load options based on input
  // timeoutId > 0 && clearTimeout(timeoutId);
  const promiseOptions = (searchKeyword) =>
    new Promise((resolve) => {
      if (timeoutId > 0) {
        clearTimeout(timeoutId);
      }
      const id = setTimeout(async () => {
        const response = await axiosInstance.post("/StudBook/FilterOwners", {
          langauge: localStorage.getItem("eahsLang") ?? "en",
          ownerId: searchKeyword,
          skip: 0,
          take: 10000,
        });
        const data = await response.data.ownersList;
        const options = data.map((item) => ({
          label: item.ownerId + " - " + item.name,
          value: item.id,
        }));
        resolve(options);
      }, 1000);

      setTimeoutId(id);
    });

  return (
    <>
      <EahsForm
        className={
          "mb-main position-relative " +
          `${canDeleteItem ? "pt-3" : ""}` +
          `${isInOwnerProfile ? " px-3" : ""}`
        }
      >
        {canDeleteItem && (
          <div
            className="btn btn-danger position-absolute w-unset text-center top-0 end-0 me-3 mt-3 border-15"
            onClick={() => removeItem(index)}
          >
            <i className="fa fa-trash w-unset" aria-hidden="true"></i>
          </div>
        )}

        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="capatalize_text">
            {translate("web.chooseOwner", "Choose Owner")}
            <i style={{ color: "red" }}>*</i>
          </label>
          <AsyncSelect
            isClearable
            loadOptions={promiseOptions}
            defaultOptions={false} // Set to true to load initial options
            onInputChange={(newValue) => setInputValue(newValue)}
            inputValue={inputValue}
            loadingMessage={() => translate("web.loading", "Loading")}
            onChange={(selectedItem) => {
              setSelectedOwner(selectedItem);
              updatePermissionClaim(index, {
                ...claim,
                selectedOwner: selectedItem,
              });
            }}
            placeholder={
              translate("web.typeToSearch", "Type To Search") + "..."
            }
            noOptionsMessage={() => translate("web.noOptions", "No Options")}
          />
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <NetaqInput
            type={"select"}
            label={translate(
              "web.chooseRequestedPermisions",
              "Choose Requested Permisions"
            )}
            placeholder={translate(
              "web.chooseRequestedPermisions",
              "Choose Requested Permisions"
            )}
            name={`premisions${index}`}
            listSelect={[selectAllOption, ...permissionsList]}
            value={selectedPermissions}
            isMulti={true}
            onChange={(selectedItem) => {
              // console.log('selectred' , selectedItem);
              let checkifAllSelected = false;
              if (
                selectedItem.filter((item) => item.value == "all").length > 0
              ) {
                checkifAllSelected = true;
              } else {
                checkifAllSelected = false;
              }

              if (checkifAllSelected) {
                // Select all options
                setSelectedPermissions(permissionsList);
                updatePermissionClaim(index, {
                  ...claim,
                  selectedPermissions: permissionsList,
                });
              } else if (
                selectedItem.length === selectedPermissions.length - 1 &&
                !selectedItem.includes(selectAllOption)
              ) {
                // Deselect all options
                setSelectedPermissions([]);
                updatePermissionClaim(index, {
                  ...claim,
                  selectedPermissions: [],
                });
              } else {
                // Update selected options without "Select All"
                setSelectedPermissions(
                  selectedItem.filter((option) => option.value !== "all")
                );
                updatePermissionClaim(index, {
                  ...claim,
                  selectedPermissions: selectedItem.filter(
                    (option) => option.value !== "all"
                  ),
                });
              }
              // setSelectedPermissions(selectedItem);
              // updatePermissionClaim(index, { ...claim, selectedPermissions: selectedItem })
            }}
            required={true}
          />
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="capatalize_text">
            {translate("web.authDocuments", "Auth Documents")}{" "}
            <span className="color-eahsred">*</span>
          </label>
          <SimpleUpload
            key={`authfile${index}`}
            className="text-center"
            onChange={(e) => verifyPoAFileSettings(e)}
            accept="image/*,application/pdf"
            isMulti={false}
            isServer={false}
            initialFiles={agentAuthFiles}
          />
        </div>

        <div
          className={
            isInOwnerProfile
              ? "col-lg-6 col-md-6 col-sm-12"
              : "col-lg-3 col-md-6 col-sm-12"
          }
        >
          <NetaqInput
            type={"normalDate"}
            label={translate("web.from", "From")}
            name={`autharizationDocumentsStartDate${index + 1}`}
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value);
              updatePermissionClaim(index, {
                ...claim,
                fromDate: e.target.value,
              });
            }}
            required={true}
          />
        </div>
        <div
          className={
            isInOwnerProfile
              ? "col-lg-6 col-md-6 col-sm-12"
              : "col-lg-3 col-md-6 col-sm-12"
          }
        >
          <NetaqInput
            type={"normalDate"}
            label={translate("web.to", "To")}
            name={`autharizationDocumentsExpiryDate${index + 1}`}
            value={toDate}
            onChange={(event) => {
              setToDate(event.target.value);
              updatePermissionClaim(index, {
                ...claim,
                toDate: event.target.value,
              });
            }}
            required={true}
            minDate={fromDate}
          />
        </div>

        {!claim.isValid && Array.isArray(claim.errors) && (
          <>
            <h4 className="col-lg-12 col-md-12 col-sm-12 text-center text-danger mt-2  rtl">
              {translate("web.pleaseFillAllFields", "Please Fill All Fields")}
            </h4>
            <div className="col-lg-12 col-md-12 col-sm-12 text-center text-danger  rtl">
              {claim.errors.map(
                (error, i) =>
                  `${translate(`web.${error}`, error)} ${
                    i + 1 < claim.errors.length ? " - " : ""
                  }`
              )}
            </div>
          </>
        )}
      </EahsForm>
    </>
  );
};

export default GrantedPermissionClaimsComponent;
